import DirectionsIcon from "@mui/icons-material/Directions";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Box, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import { GoogleMap } from "@react-google-maps/api";
import { Circle, Marker } from "../../../../theme/maps";
import { useMemo } from "react";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { colors } from "../../../ui/colors";
import { CoverageArea } from "../../interfaces/job.interfaces";
import { SelectJobCoverageStep } from "../components/SelectJobCoverageStep";

const CONTEXT_BLOCK_HEIGHT = 320;

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  clickableIcons: false,
  tilt: 45,
  styles: [
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { elementType: "labels", stylers: [{ visibility: "off" }] },
    { elementType: "geometry", stylers: [{ saturation: -35 }, { lightness: 10 }] },
    { featureType: "road", stylers: [{ saturation: -60 }, { lightness: 25 }] },
    { featureType: "water", stylers: [{ saturation: -30 }, { lightness: 20 }] },
  ],
};

interface Props {
  coverageArea?: CoverageArea | null;
  fullWidth?: boolean;
  jobId?: string | number;
  clientUid?: string | null;
}

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const getValidLatLng = (latitude: unknown, longitude: unknown): google.maps.LatLngLiteral | null => {
  const lat = toFiniteNumber(latitude);
  const lng = toFiniteNumber(longitude);

  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;

  return { lat, lng };
};

const getCoverageRadiusMeters = (milesValue: unknown): number | null => {
  const miles = toFiniteNumber(milesValue);

  if (miles === null || miles <= 0) return null;

  return miles * 1609.34;
};

const buildAddress = (coverageArea?: CoverageArea | null) => {
  const parts = [coverageArea?.address, coverageArea?.city, coverageArea?.state, coverageArea?.zipCode]
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  return parts.join(", ");
};

export function JobCoverageArea({ coverageArea, fullWidth = false, jobId, clientUid }: Props) {
  const center = useMemo(() => getValidLatLng(coverageArea?.latitude, coverageArea?.longitude), [coverageArea?.latitude, coverageArea?.longitude]);

  const radiusMeters = useMemo(() => getCoverageRadiusMeters(coverageArea?.milesOfCoverageArea), [coverageArea?.milesOfCoverageArea]);

  const fullAddress = useMemo(() => buildAddress(coverageArea), [coverageArea]);

  const mapsUrl = center ? `https://www.google.com/maps/search/?api=1&query=${center.lat},${center.lng}` : undefined;

  const directionsUrl = center ? `https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}` : undefined;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: fullWidth ? "100%" : "55%" },
        minWidth: fullWidth ? 0 : { md: 420 },
        flex: fullWidth ? 1 : undefined,
        p: 2,
        height: CONTEXT_BLOCK_HEIGHT,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 500,
            color: colors.gray[700],
          }}
        >
          Address
        </Typography>

        {clientUid && jobId ? (
          <BaseModalComponent
            modalKey={`Job-Edit-Coverage-${jobId}`}
            modalLabel="Edit coverage area"
            defaultSizes={{ width: 900 }}
            elementTrigger={
              <Tooltip title="Edit coverage area">
                <IconButton
                  size="small"
                  aria-label="Edit coverage area"
                  sx={{
                    width: 28,
                    height: 28,
                    color: colors.blue[500],
                    "&:hover": {
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            }
          >
            <SelectJobCoverageStep jobId={jobId} clientUidSelected={clientUid} currentCoverageAreaId={coverageArea?.id} />
          </BaseModalComponent>
        ) : null}
      </Stack>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: colors.gray[50],
          border: `1px solid ${colors.gray[200]}`,
        }}
      >
        {center ? (
          <>
            <GoogleMap mapContainerStyle={MAP_CONTAINER_STYLE} center={center} zoom={14} options={MAP_OPTIONS}>
              <Circle
                center={center}
                radius={500}
                options={{
                  fillColor: "#E28743",
                  fillOpacity: 0.15,
                  strokeColor: "#E28743",
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  clickable: false,
                  draggable: false,
                  editable: false,
                  zIndex: 9999,
                }}
              />

              <Marker
                position={center}
                options={{
                  icon: {
                    url: "https://maps.google.com/mapfiles/ms/micons/orange.png",
                    scaledSize: new google.maps.Size(32, 32),
                  },
                }}
              />

              {radiusMeters && (
                <Circle
                  center={center}
                  radius={radiusMeters}
                  options={{
                    fillColor: "#3b82f6",
                    fillOpacity: 0.08,
                    strokeColor: "#3b82f6",
                    strokeOpacity: 0.25,
                    strokeWeight: 1,
                  }}
                />
              )}
            </GoogleMap>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
              }}
            >
              {directionsUrl && (
                <Tooltip title="Open directions">
                  <IconButton
                    size="small"
                    component="a"
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                      border: `1px solid ${colors.gray[200]}`,
                      width: 32,
                      height: 32,
                      "&:hover": { backgroundColor: "var(--fx-surface-base, #FFFFFF)" },
                    }}
                  >
                    <DirectionsIcon sx={{ fontSize: "0.95rem", color: colors.gray[700] }} />
                  </IconButton>
                </Tooltip>
              )}

              {mapsUrl && (
                <Tooltip title="View full address">
                  <IconButton
                    size="small"
                    component="a"
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                      border: `1px solid ${colors.gray[200]}`,
                      width: 32,
                      height: 32,
                      "&:hover": { backgroundColor: "var(--fx-surface-base, #FFFFFF)" },
                    }}
                  >
                    <OpenInFullIcon sx={{ fontSize: "0.9rem", color: colors.gray[700] }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: colors.gray[500],
                lineHeight: 1.4,
              }}
            >
              Coverage map is not available yet.
            </Typography>
          </Box>
        )}
      </Box>

      {mapsUrl ? (
        <Link
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={fullAddress || "Open location in Google Maps"}
          sx={{
            mt: 1.5,
            fontSize: "0.8rem",
            color: colors.blue[500],
            lineHeight: 1.4,
            underline: "hover",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
          }}
        >
          {fullAddress || "Open location in Google Maps"}
        </Link>
      ) : (
        <Typography
          title={fullAddress || "Address unavailable"}
          sx={{
            mt: 1.5,
            fontSize: "0.8rem",
            color: colors.gray[500],
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {fullAddress || "Address unavailable"}
        </Typography>
      )}
    </Box>
  );
}
